import { useMutation } from "react-query";
import {
  ACTIVATION_MODAL_KEY,
  PREVIEW_MODAL_KEY,
  useSessionStorage,
} from "react/components/NewVersionOptIn/OptInLandingPage/hooks/useSessionStorage";
import { reactQueryPost } from "react/helpers/backendRequestHelpers";
import { internalApi } from "react/helpers/urlHelpers";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

import { useActivationLoadingScreenState } from "../ActivationLoadingScreen/useActivationLoadingScreenState";

interface UseV4ActivationOptions {
  minimumLoadingDurationMs?: number;
  onError?: () => void;
  onSuccess?: () => void;
  redirectTo?: string;
  showActivationLoadingScreen?: boolean;
  showPreviewModal?: boolean;
  showSuccessModal?: boolean;
}

export const useV4Activation = ({
  minimumLoadingDurationMs,
  onSuccess,
  onError,
  redirectTo = "/feed",
  showPreviewModal = false,
  showSuccessModal = false,
  showActivationLoadingScreen = false,
}: UseV4ActivationOptions = {}) => {
  const toast = useToast();
  const { set: setShowActivationModal } =
    useSessionStorage<boolean>(ACTIVATION_MODAL_KEY);
  const { set: setShowPreviewModal } =
    useSessionStorage<boolean>(PREVIEW_MODAL_KEY);
  const {
    clear: clearActivationLoadingScreen,
    start: startActivationLoadingScreen,
  } = useActivationLoadingScreenState();

  const { mutate, isPending: isActivating } = useMutation({
    mutationFn: async () => {
      const activationPromise = reactQueryPost(
        internalApi.settings.v4_ui_preference.activate(),
      );

      if (!minimumLoadingDurationMs) {
        return activationPromise;
      }

      const [result] = await Promise.all([
        activationPromise,
        new Promise<void>(resolve =>
          setTimeout(resolve, minimumLoadingDurationMs),
        ),
      ]);

      return result;
    },
    onMutate: () => {
      if (showActivationLoadingScreen) {
        startActivationLoadingScreen();
      }
    },
    onSuccess: () => {
      if (showSuccessModal) {
        setShowActivationModal(true);
      }
      if (showPreviewModal) {
        setShowPreviewModal(true);
      }
      onSuccess?.();
      window.location.href = redirectTo;
    },
    onError: () => {
      if (showActivationLoadingScreen) {
        clearActivationLoadingScreen();
      }
      toast.error(t("failed_to_save_changes"));
      onError?.();
    },
  });

  return {
    mutate,
    isActivating,
  };
};
